import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TbPlus, TbCopy, TbTrash, TbEye, TbPencil, TbFileText } from "react-icons/tb";
import { useTemplates } from "../../hooks/useTemplates";
import { normalizeTemplateId } from "../../../services/templateService";
import { Button, Alert, EmptyState, Modal, Field, ConfirmModal } from "../../components";
import TemplateThumb from "./TemplateThumb";
import PickScanModal from "./PickScanModal";
import "./Templates.css";

// Report templates.
//
// The builder owns creating and editing; this screen is the library in front of it.
// Every action navigates to /report-builder with a state shape the builder expects —
// four distinct shapes, kept verbatim, because the builder branches on them.

// Yours first: the tenant's own templates are what someone opened this screen for. The
// defaults are a starting point they consult occasionally, and All is the fallback for
// when they cannot remember which bucket something is in. Leading with All made an
// undifferentiated pile the landing state.
//
// The middle tab is ONE tab named for the tier the tenant is on, not two tabs listing
// both. Neither tier loses access to the other set — All still holds everything, with
// pro defaults locked for a community tenant.
const viewsFor = (isPro) => [
  { key: "yours", label: "Yours" },
  isPro
    ? { key: "pro", label: "Pro defaults" }
    : { key: "community", label: "Community defaults" },
  { key: "all", label: "All" },
];

const TemplateHubScreen = () => {
  const navigate = useNavigate();
  const { groups, loading, error, isPro, busyId, remove, clone } = useTemplates();
  const [view, setView] = useState("yours");
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [cloning, setCloning] = useState(null);
  const [reportFrom, setReportFrom] = useState(null);

  const views = useMemo(() => viewsFor(isPro), [isPro]);

  // isPro resolves after the first render, so the middle tab can change identity under
  // someone who already clicked it. Without this, they are left on a list with no tab
  // highlighted and no obvious way back.
  useEffect(() => {
    if (!views.some((v) => v.key === view)) setView("yours");
  }, [views, view]);

  const rows = useMemo(() => {
    const list = groups[view] || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) => (t.name || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q));
  }, [groups, view, search]);

  // The four navigation shapes the builder branches on. Changing any of these silently
  // drops the user into the wrong mode.
  const createNew = () => navigate("/report-builder", { state: { mode: "template", scanData: null, fromTemplateHub: true } });
  const edit = (t) => navigate("/report-builder", { state: { mode: "template", templateId: normalizeTemplateId(t), fromTemplateHub: true } });
  const preview = (t) => navigate("/report-builder", { state: { mode: "template", templateId: normalizeTemplateId(t), fromTemplateHub: true, previewOnly: true } });
  // The inverse case: template known, scan not. Ask only for the scan, then go straight
  // to the builder — the old route dropped the templateId on the floor.
  const startReportFrom = (t) => setReportFrom(t);
  const openReport = (scanPath) => {
    navigate("/report-builder", {
      state: {
        fileUrl: scanPath,
        internalScanPath: scanPath,
        externalScanPath: null,
        templateId: normalizeTemplateId(reportFrom),
        mode: "report",
        hasInternalData: true,
        hasExternalData: false,
      },
    });
  };

  // A default published by the console cannot be edited or deleted by a tenant; it can
  // be cloned and the copy edited. Showing the buttons disabled rather than hiding them
  // makes that rule visible instead of leaving people wondering where the option went.
  const isDefault = (t) => t.is_community_default || t.is_pro_default;
  const locked = (t) => t.is_pro_default && !isPro;

  const pageCount = (t) => t?.configuration?.visualTemplate?.pages?.length || t?.metadata?.totalPages;

  return (
    <div className="v2-fullscreen">
      <div className="v2-tplbar">
        <input className="v2-input" style={{ maxWidth: "18rem" }} type="search" value={search}
               onChange={(e) => setSearch(e.target.value)} placeholder="Search templates" aria-label="Search templates" />
        <Button variant="primary" style={{ marginLeft: "auto" }} onClick={createNew}>
          <TbPlus size={15} /> New template
        </Button>
      </div>

      <div className="v2-views">
        {views.map((v) => (
          <button key={v.key} type="button" className={`v2-view${view === v.key ? " on" : ""}`} onClick={() => setView(v.key)}>
            {v.label}<span className="n">{(groups[v.key] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="v2-page" style={{ flex: 1 }}>
        <Alert>{error}</Alert>

        {loading ? (
          <div className="v2-tpl-grid">
            {[...Array(4)].map((_, i) => <div key={i} className="v2-tpl v2-skel"><span style={{ height: 120 }} /></div>)}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title={search ? "No matches" : "No templates here"}
            action={!search && view === "yours" ? <Button variant="primary" onClick={createNew} style={{ marginTop: ".75rem" }}>New template</Button> : null}>
            {search ? `Nothing matches "${search}".` : "Templates published by the console appear under the defaults tab."}
          </EmptyState>
        ) : (
          <div className="v2-tpl-grid">
            {rows.map((t) => (
              <article key={t.id} className={`v2-tpl${locked(t) ? " locked" : ""}`}>
                <TemplateThumb template={t} />
                <div className="v2-tpl-body">
                  <div className="v2-tpl-head">
                    <span className="v2-tpl-name">{t.name}</span>
                    {t.is_pro_default && <span className="v2-badge v2-badge--pro">PRO</span>}
                    {t.is_community_default && <span className="v2-badge">DEFAULT</span>}
                  </div>
                  <span className="v2-tpl-meta">
                    {pageCount(t) ? `${pageCount(t)} pages in the layout` : "Predefined layout"}
                  </span>
                  <div className="v2-tpl-actions">
                    <Button icon title="Preview" onClick={() => preview(t)}><TbEye size={15} /></Button>
                    <Button icon title="Use for a report" onClick={() => startReportFrom(t)}><TbFileText size={15} /></Button>
                    <Button icon title={isDefault(t) ? "Clone to edit" : "Edit"} disabled={isDefault(t)} onClick={() => edit(t)}>
                      <TbPencil size={15} />
                    </Button>
                    <Button icon title="Clone" disabled={busyId === t.id} onClick={() => setCloning(t)}><TbCopy size={15} /></Button>
                    <Button icon variant="danger" title={isDefault(t) ? "Defaults cannot be deleted" : "Delete"}
                            disabled={isDefault(t) || busyId === t.id} onClick={() => setConfirm(t)}>
                      <TbTrash size={15} />
                    </Button>
                  </div>
                </div>
                {locked(t) && <div className="v2-tpl-lock">Pro</div>}
              </article>
            ))}
          </div>
        )}

        <p className="v2-hint" style={{ marginTop: ".875rem", maxWidth: "70ch" }}>
          Thumbnails are drawn from each template's stored block positions, so they need no scan
          data. Page counts are the source layout: the scan adds continuation pages at export
          time, so an exported file is usually longer.
        </p>
      </div>

      {reportFrom && (
        <PickScanModal template={reportFrom} onClose={() => setReportFrom(null)} onPick={openReport} />
      )}
      {cloning && <CloneModal template={cloning} onClose={() => setCloning(null)} onClone={clone} />}
      {confirm && (
        <ConfirmModal title="Delete this template?"
          body={`"${confirm.name}" will be removed permanently. Reports already exported are unaffected.`}
          busy={busyId === confirm.id}
          onClose={() => setConfirm(null)}
          onConfirm={async () => { await remove(confirm); setConfirm(null); }} />
      )}
    </div>
  );
};

const CloneModal = ({ template, onClose, onClone }) => {
  const [name, setName] = useState(`${template.name} copy`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!name.trim()) { setErr("Give the copy a name."); return; }
    setBusy(true); setErr("");
    try { await onClone(template, name.trim()); onClose(); }
    catch (e) { setErr(e.message || "Could not clone the template."); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Clone template" onClose={onClose}
      footer={<>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Cloning…" : "Clone"}</Button>
      </>}>
      <Alert>{err}</Alert>
      <Field label="Name for the copy" hint="Cloning a default is how you get an editable version of it.">
        <input className="v2-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
};

export default TemplateHubScreen;
